import { Button, Col, Empty, Row, Tag, Tooltip } from 'antd';
import React from 'react';
import styled from 'styled-components/macro';
import { StyledTable } from '../../entity/shared/components/styled/StyledTable';
import { CLI_EXECUTOR_ID } from './utils';
import {
    TypeColumn,
    ActionsColumn,
    LastExecutionColumn,
    CurrentStatusColumn,
    PREFIX_REMOVE_INGESTION,
} from './IngestionSourceTableColumns';
import { IngestionSource } from '../../../types.generated';
import { IngestionSourceExecutionList } from './executions/IngestionSourceExecutionList';
import { useTranslation } from '../../../i18n/useTranslation';
import { ReactComponent as IconArrowDown } from '../../../images/keyboard_arrow_down.svg';
import { AutoTooltip } from '../../entity/shared/components/common/AutoTooltip';
import { IngestionPrivileges } from '../../entity/shared/rbacConstantsService';

const StyledSourceTable = styled(StyledTable)`
    .ant-table-thead {
        .ant-table-cell {
            border-radius: 0 !important;
        }
    }
` as typeof StyledTable;

const TableActionHeader = styled.div`
    text-align: right;
`;

interface Props {
    lastRefresh: number;
    sources: IngestionSource[];
    setFocusExecutionUrn: (urn: string) => void;
    onExecute: (urn: string) => void;
    onEdit: (urn: string) => void;
    onView: (urn: string) => void;
    onDelete: (urn: string) => void;
    onRefresh: () => void;
    privileges: IngestionPrivileges;
}

function IngestionSourceTable({
    lastRefresh,
    sources,
    setFocusExecutionUrn,
    onExecute,
    onEdit,
    onView,
    onDelete,
    onRefresh,
    privileges,
}: Props) {
    const { t } = useTranslation();
    const tableColumns = [
        {
            title: t('Name'),
            dataIndex: 'displayName',
            key: 'displayName',
            render: (name: string, record: any) => (
                <Row align="middle" className="f-gap-2">
                    <Col flex="none">
                        <AutoTooltip
                            tooltipPlacement="topLeft"
                            style={{ maxWidth: 300 }}
                            className="f-text-medium-content f-color-dark-black-s100"
                        >
                            {name.replace(PREFIX_REMOVE_INGESTION.DATA_HUB, '') || ''}
                        </AutoTooltip>
                    </Col>
                    {record.cliIngestion && (
                        <Col flex="none">
                            <Tooltip title="This source is ingested from the command-line interface (CLI)">
                                <Tag className="label-tag base-tag ma-0">CLI</Tag>
                            </Tooltip>
                        </Col>
                    )}
                </Row>
            ),
        },
        {
            title: t('Type'),
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => <TypeColumn type={type} />,
        },
        {
            title: t('Execution Count'),
            dataIndex: 'execCount',
            key: 'execCount',
            render: (execCount: any) => (
                <div className="f-left-border-highlight f-height-auto py-0">{execCount || '0'}</div>
            ),
        },
        {
            title: t('Current Status'),
            dataIndex: 'currentStatus',
            key: 'currentStatus',
            render: (status: any, record) => <CurrentStatusColumn status={status} failedReason={record.failedReason} />,
        },
        {
            title: t('Last Execution'),
            dataIndex: 'lastExecTime',
            key: 'lastExecTime',
            render: LastExecutionColumn,
        },
        {
            title: <TableActionHeader>{t('Actions')}</TableActionHeader>,
            dataIndex: '',
            key: 'x',
            render: (_, record: any) => (
                <ActionsColumn
                    record={record}
                    setFocusExecutionUrn={setFocusExecutionUrn}
                    onExecute={onExecute}
                    onDelete={onDelete}
                    onView={onView}
                    onEdit={onEdit}
                    canUpdate={privileges.canUpdate}
                    canDelete={privileges.canDelete}
                    canExecute={privileges.canExecute}
                />
            ),
        },
    ];

    const tableData = sources.map((source) => ({
        urn: source.urn,
        type: source.type,
        name: source.name,
        recipeVersion: source.recipeVersion ?? 'v1',
        displayName: source.displayName,
        platformUrn: source.platform?.urn,
        schedule: source.schedule?.interval,
        currentStatus: source.currentStatus,
        failedReason: source.failedReason,
        timezone: source.schedule?.timezone,
        execCount: source.executions?.total || 0,
        lastExecUrn:
            source.executions &&
            source.executions?.executionRequests.length > 0 &&
            source.executions?.executionRequests[0].urn,
        lastExecTime:
            source.executions &&
            source.executions?.executionRequests.length > 0 &&
            source.executions?.executionRequests[0].result?.startTimeMs,
        lastExecStatus:
            source.executions &&
            source.executions?.executionRequests.length > 0 &&
            source.executions?.executionRequests[0].result?.status,
        cliIngestion: source.config?.executorId === CLI_EXECUTOR_ID,
        cliIngestionByAppliance: !!source?.applianceId,
    }));

    return (
        <StyledSourceTable
            rowKey="urn"
            columns={tableColumns}
            dataSource={tableData}
            locale={{
                emptyText: <Empty description={t('No Ingestion Sources!')} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
            }}
            expandable={{
                expandedRowRender: (record, _index, _indent, expanded) => {
                    return (
                        <IngestionSourceExecutionList
                            urn={record.urn}
                            isExpanded={expanded}
                            lastRefresh={lastRefresh}
                            onRefresh={onRefresh}
                            canCancel={privileges.canCancel}
                        />
                    );
                },
                rowExpandable: (record) => {
                    return record.execCount > 0;
                },
                defaultExpandAllRows: false,
                indentSize: 0,
                // eslint-disable-next-line react/prop-types
                expandIcon: ({ expanded, onExpand, record }) => {
                    // eslint-disable-next-line react/prop-types
                    if (record.execCount <= 0) return null;
                    return (
                        <Button
                            onClick={(e) => onExpand(record, e as any)}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                            className={`icon-button ma-0 ${expanded ? 'f-expanded' : ''}`}
                        >
                            <IconArrowDown />
                        </Button>
                    );
                },
            }}
            pagination={false}
        />
    );
}

export default IngestionSourceTable;
